import commandFixture from '../../../../fixtures/point-in-time/command.json';
import marketsPanels from '../../../../fixtures/markets/panels.json';
import worldFixture from '../../../../fixtures/events/world.json';
import capitalEntity from '../../../../fixtures/capital/tps-entity.json';
import acmDossier from '../../../../fixtures/securities/acm-dossier.json';
import acmValuation from '../../../../fixtures/valuations/acm-golden.json';
import portfolioHousehold from '../../../../fixtures/portfolio/household.json';
import thesesIndex from '../../../../fixtures/theses/index.json';
import learningOverview from '../../../../fixtures/learning/overview.json';
import learningCounterfactuals from '../../../../fixtures/learning/counterfactuals.json';
import librarySources from '../../../../fixtures/library/sources.json';
import operationsFreshness from '../../../../fixtures/operations/freshness.json';
import { GLOSSARY_REGISTRY } from '@atlas/glossary';

const FIXTURE = import.meta.env.VITE_FIXTURE_MODE === 'true' || import.meta.env.DEV;

const fixtures: Record<string, unknown> = {
  '/api/v1/command': commandFixture,
  '/api/v1/markets/panels': marketsPanels,
  '/api/v1/world/events': worldFixture,
  '/api/v1/world/scenarios': { scenarios: worldFixture.scenarios },
  '/api/v1/portfolio': portfolioHousehold,
  '/api/v1/theses': thesesIndex,
  '/api/v1/learning/overview': {
    ...learningOverview,
    proxySeparated: true,
    counterfactuals: learningCounterfactuals.counterfactuals,
  },
  '/api/v1/glossary': { entries: GLOSSARY_REGISTRY },
  '/api/v1/operations/freshness': operationsFreshness,
  '/api/v1/library/search': { results: librarySources.sources },
  '/api/v1/securities/acm': { ...acmDossier, valuation: acmValuation },
  '/api/v1/capital/entities/acm': capitalEntity,
};

async function fetchJson<T>(path: string): Promise<T> {
  if (FIXTURE && fixtures[path]) {
    return fixtures[path] as T;
  }
  const API_BASE = import.meta.env.VITE_API_BASE ?? '';
  try {
    const res = await fetch(`${API_BASE}${path}`, { headers: { Accept: 'application/json' } });
    if (!res.ok) {
      if (fixtures[path]) return fixtures[path] as T;
      throw new Error(`API ${path}: ${res.status}`);
    }
    return res.json() as Promise<T>;
  } catch {
    if (fixtures[path]) return fixtures[path] as T;
    throw new Error(`API ${path}: unavailable`);
  }
}

export type CommandData = typeof commandFixture;

export const api = {
  command: () => fetchJson<CommandData>('/api/v1/command'),
  panels: () => fetchJson<typeof marketsPanels>('/api/v1/markets/panels'),
  worldEvents: () => fetchJson<typeof worldFixture>('/api/v1/world/events'),
  playerMap: (eventId: string) =>
    fetchJson<{ principals: Array<Record<string, string>> }>(
      `/api/v1/world/events/${eventId}/player-map`,
    ).catch(() => ({
      principals:
        worldFixture.playerMaps[eventId as keyof typeof worldFixture.playerMaps]?.principals ?? [],
    })),
  scenarios: () => fetchJson<{ scenarios: typeof worldFixture.scenarios }>('/api/v1/world/scenarios'),
  portfolio: () => fetchJson<typeof portfolioHousehold>('/api/v1/portfolio'),
  theses: () => fetchJson<typeof thesesIndex>('/api/v1/theses'),
  learning: () =>
    fetchJson<typeof learningOverview & { proxySeparated: boolean; counterfactuals: unknown[] }>(
      '/api/v1/learning/overview',
    ),
  glossary: () => fetchJson<{ entries: typeof GLOSSARY_REGISTRY }>('/api/v1/glossary'),
  glossaryEntry: (slug: string) => {
    const entry = GLOSSARY_REGISTRY.find((e) => e.slug === slug);
    if (!entry) throw new Error(`Glossary entry not found: ${slug}`);
    return Promise.resolve(entry);
  },
  glossarySearch: (q: string) =>
    fetchJson<{ results: typeof GLOSSARY_REGISTRY }>(
      `/api/v1/glossary/search?q=${encodeURIComponent(q)}`,
    ).catch(() => ({
      results: GLOSSARY_REGISTRY.filter(
        (e) =>
          e.term.toLowerCase().includes(q.toLowerCase()) ||
          e.slug.includes(q.toLowerCase()) ||
          e.aliases.some((a) => a.toLowerCase().includes(q.toLowerCase())),
      ),
    })),
  operations: () => fetchJson<typeof operationsFreshness>('/api/v1/operations/freshness'),
  library: (q: string) =>
    fetchJson<{ results: typeof librarySources.sources }>(
      `/api/v1/library/search?q=${encodeURIComponent(q)}`,
    ).catch(() => ({
      results: librarySources.sources.filter((s) =>
        s.title.toLowerCase().includes(q.toLowerCase()),
      ),
    })),
  security: (id: string) =>
    fetchJson<typeof acmDossier & { valuation?: typeof acmValuation }>(
      `/api/v1/securities/${id}`,
    ).catch(() => (id === 'acm' ? (fixtures['/api/v1/securities/acm'] as typeof acmDossier) : Promise.reject())),
  capital: (id: string) =>
    fetchJson<typeof capitalEntity>(`/api/v1/capital/entities/${id}`).catch(() =>
      id === 'acm' ? capitalEntity : Promise.reject(),
    ),
};
